import React, {useEffect} from 'react';
import {
  Modal,
  Select,
  Button,
  Divider,
  Radio,
  Tooltip,
  Input,
  Popconfirm,
  Alert,
} from 'antd';
import {
  JobStatus,
  formatCurrencyByLocalChapter,
  maxTagPlaceholder,
} from '../../../Util/commonUtility';
import './style.css';
import {FileTextOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {useSelector, useDispatch} from 'react-redux';
import {getAllChapters} from './../../../Store/Actions/chapters';
import useLocalization from '../../../Localization';
import CancellationPolicyModal from '../MyEventListing/MyEventCard/CancellationPolicy';

/** Refund preview amounts — show cents exactly (no whole-dollar rounding). */
const formatRefundAmount = (value, chapterData) =>
  formatCurrencyByLocalChapter(value, chapterData, 2, 2);

const formatProcessingFeePercentageLabel = (percentage) => {
  const value = Number(percentage);
  if (!Number.isFinite(value) || value <= 0) return '';
  return parseFloat(value.toFixed(2)).toString();
};

function StatusUpdateModal({
  visible,
  onCancel,
  showUpdateStatus,
  handleChange,
  handleUpdateJobStatus,
  submitText,
  statusHeading,
  modalTitle,
  selectionOptions,
  tagRender,
  mode,
  setRefundToAccount,
  refundToAccount = false,
  handleRadioChange,
  refundBoolForAdmin = false,
  isColorSchemeUser,
  data,
  selectedRowProcessingFeePercentage,
  selectedRowProcessingFee,
  isModalCentred,
  width,
  handleRefundAdminInputChange,
  percentageRefund,
  refundMode = 'percentage',
  handleRefundModeChange,
  exactAmountRefund,
  handleRefundAmountChange,
  selectedRowTotalRefundAmount = 0,
  usedForMyEvents = false,
  showCancellationPolicy = true,
  selectedRowCreditAmount,
  selectedRowPaidAmount,
  selectedRowPaidAmountWithFee,
  selectedRowStripePaymentIds = [],
  selectedRowTotalDiscount,
  localChapterData,
  creditOnlyRefund = false,
  submitLoading = false,
}) {
  const {ApplicationString} = useLocalization();

  useEffect(() => {
    if (visible && creditOnlyRefund && setRefundToAccount) {
      setRefundToAccount(true);
    }
  }, [visible, creditOnlyRefund, setRefundToAccount]);

  return (
    <>
      <Modal
        open={visible}
        onCancel={onCancel}
        closable={!submitLoading}
        maskClosable={!submitLoading}
        centered={isModalCentred}
        width={width || 520}
        title={
          usedForMyEvents
            ? modalTitle || (showCancellationPolicy ? '' : statusHeading)
            : statusHeading
            ? statusHeading
            : 'Update Status'
        }
        footer={
          <div className='update-footer'>
            <Button key='cancel' onClick={onCancel} disabled={submitLoading}>
              {ApplicationString['commonModal.cancel']}
            </Button>

            {refundBoolForAdmin ? (
              <Popconfirm
                placement='topRight'
                title='Refund Selected Registration'
                prefixCls='status-modal-popconfirm'
                description='Are you sure you want to refund the selected attendees?'
                onConfirm={handleUpdateJobStatus}
                disabled={submitLoading}
                okType='primary'
                icon={<QuestionCircleOutlined style={{color: 'warning'}} />}>
                <Button
                  key='update'
                  type='primary'
                  className='common-button'
                  loading={submitLoading}>
                  {submitText ? submitText : 'Update'}
                </Button>
              </Popconfirm>
            ) : (
              <Button
                key='update'
                type='primary'
                onClick={handleUpdateJobStatus}
                className={
                  isColorSchemeUser ? 'common-button-refund' : 'common-button'
                }>
                {submitText ? submitText : 'Update'}
              </Button>
            )}
          </div>
        }>
        {usedForMyEvents && (
          <>
            {showCancellationPolicy && (
              <>
                <CancellationPolicyModal />
                <Divider />
              </>
            )}
            <h4 style={{fontSize: '16px', fontWeight: 600}}>
              {statusHeading ? statusHeading : 'Update Status'}
            </h4>
            {setRefundToAccount && (
              <Alert
                message={'Processing fee will not be refunded.'}
                style={{marginBottom: '10px'}}
                type='info'
                showIcon
              />
            )}
          </>
        )}

        {usedForMyEvents ? ' ' : <Divider />}

        <div className='refund-modal-fields-wrapper'>
          {refundBoolForAdmin ? (
            <>
              <div className='refund-modal-radio-wrapper'>
                <p>Refund Type</p>
                <Radio.Group
                  prefixCls='refund-radio-group'
                  onChange={handleRefundModeChange}
                  value={refundMode}
                  buttonStyle='solid'>
                  <Radio.Button value='percentage'>Percentage</Radio.Button>
                  <Radio.Button value='amount'>Exact Amount</Radio.Button>
                </Radio.Group>
              </div>
              {refundMode === 'amount' ? (
                <>
                  <span>
                    Please enter the total amount to be refunded. It will be
                    split equally across the selected attendees.
                  </span>
                  <Input
                    prefixCls='refund-admin-input'
                    value={exactAmountRefund}
                    placeholder='Refund'
                    prefix={(0)
                      .toLocaleString(undefined, {
                        style: 'currency',
                        currency: localChapterData?.local_currency || 'USD',
                      })
                      .replace(/[\d.,\s]/g, '')}
                    onChange={handleRefundAmountChange}
                  />
                </>
              ) : (
                <>
                  <span>
                    Please enter the percentage of the amount to be refunded.
                  </span>
                  <Input
                    prefixCls='refund-admin-input'
                    value={percentageRefund}
                    placeholder='Refund'
                    suffix='%'
                    onChange={handleRefundAdminInputChange}
                  />
                </>
              )}
              <div>
                <span>
                  Paid Amount :{' '}
                  {formatRefundAmount(
                    selectedRowPaidAmountWithFee,
                    localChapterData,
                  )}
                </span>
                <br />
                <span>
                  Paid Registration Fee :{' '}
                  {formatRefundAmount(selectedRowPaidAmount, localChapterData)}
                </span>
                <br />
                <span>
                  Paid Credit Card Processing Fee
                  {(() => {
                    const pct = formatProcessingFeePercentageLabel(
                      selectedRowProcessingFeePercentage,
                    );
                    return pct ? ` (${pct}%)` : '';
                  })()}
                  {' : '}
                  {formatRefundAmount(
                    selectedRowProcessingFee,
                    localChapterData,
                  )}
                </span>
                <br />
                <span>
                  Used Credit :
                  {formatRefundAmount(
                    selectedRowCreditAmount,
                    localChapterData,
                  )}
                </span>
                <br />
                <span>
                  Coupon Discount :
                  {formatRefundAmount(
                    selectedRowTotalDiscount,
                    localChapterData,
                  )}
                </span>
                <br />
                <span>
                  Stripe Payment ID :{' '}
                  {selectedRowStripePaymentIds?.length
                    ? selectedRowStripePaymentIds.join(', ')
                    : 'N/A'}
                </span>
                <br />
                <hr />
                <span>
                  <b>
                    Total Amount to be Refunded :
                    {formatRefundAmount(
                      selectedRowTotalRefundAmount,
                      localChapterData,
                    )}
                  </b>
                </span>
                <br />
                <br />
                <Alert
                  message={'Coupon Discount payment will not be refunded.'}
                  type='info'
                  showIcon
                />
              </div>
            </>
          ) : (
            <Select
              prefixCls='common-select-register'
              maxTagCount={'responsive'}
              maxTagPlaceholder={maxTagPlaceholder}
              className='update-status-selection'
              placeholder='Select option'
              value={showUpdateStatus}
              defaultValue={showUpdateStatus}
              onChange={handleChange}
              options={selectionOptions}
              mode={mode}
              tagRender={tagRender}
            />
          )}
          {refundBoolForAdmin && (
            <div className='refund-modal-radio-wrapper'>
              <p>
                Direct or Credit{' '}
                <Tooltip
                  title={`For direct payments, the funds will be returned to the original payment method. For credit payments, the funds will be added to the user's credit balance.`}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </p>
              {creditOnlyRefund ? (
                <>
                  <Radio.Group
                    prefixCls='refund-radio-group'
                    value='credit'
                    buttonStyle='solid'>
                    <Radio.Button value='credit'>Credit</Radio.Button>
                  </Radio.Group>
                  <Alert
                    message='This registration was paid with account credit only, so the refund will return to credit.'
                    type='info'
                    showIcon
                    style={{marginTop: '10px'}}
                  />
                </>
              ) : (
                <Radio.Group
                  prefixCls='refund-radio-group'
                  onChange={handleRadioChange}
                  value={refundToAccount ? 'credit' : 'direct'}
                  buttonStyle='solid'>
                  <Radio.Button value='direct'>Direct</Radio.Button>
                  <Radio.Button value='credit'>Credit</Radio.Button>
                </Radio.Group>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export default StatusUpdateModal;
